import { Delete } from "@mui/icons-material";
import Edit from "@mui/icons-material/Edit";
import { Box, Typography } from "@mui/material";
import { ReactElement } from "react";

import Table, { DeleteManyState } from "~frontend/components/Table";
import TagChip from "~frontend/components/TagChip";
import { ListTagsTagOrGroup, TagOrGroupDetail } from "~frontend/graphql/tags";
import { palette } from "~frontend/theme";

import { renderActions, renderIntegrations } from "./tagTableRenderers";

export default function TagTable({
  rows,
  page,
  onChangePage,
  rowsPerPage,
  onChangeRowsPerPage,
  totalCount,
  onClickEdit,
  onClickDelete,
  deleteManyState,
  onChangeDeleteManyState,
}: {
  rows: TagOrGroupDetail[];
  page: number;
  onChangePage: (newPage: number) => void;
  rowsPerPage: number;
  onChangeRowsPerPage: (newRowsPerPage: number) => void;
  totalCount: number;
  onClickEdit: (row: TagOrGroupDetail) => void;
  onClickDelete: (row: TagOrGroupDetail) => void;
  deleteManyState: DeleteManyState;
  onChangeDeleteManyState: (newState: DeleteManyState) => void;
}): ReactElement {
  return (
    <Table
      columns={[
        {
          key: "name",
          title: "Tag Name",
          renderValue: nestedTagTableRenderName,
        },
        {
          key: "integration",
          title: "Integration",
          renderValue: tagOrGroup =>
            tagOrGroup.__typename === "Tag"
              ? renderIntegrations(tagOrGroup)
              : null,
          align: "left",
        },
        {
          key: "actions",
          title: "Tag Action",
          renderValue: tagOrGroup =>
            tagOrGroup.__typename === "Tag" ? renderActions(tagOrGroup) : null,
          align: "left",
        },
      ]}
      rows={rows}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      page={page}
      onChangePage={onChangePage}
      rowActions={tagOrGroup => [
        {
          id: "edit",
          icon: <Edit />,
          tooltipLabel: "Edit",
          onClick: () => onClickEdit(tagOrGroup),
        },
        {
          id: "delete",
          icon: <Delete />,
          tooltipLabel: "Delete",
          confirm: {
            title: "Delete Tag",
            content: `Are you sure you want to delete the "${tagOrGroup.name}" tag?`,
          },
          onClick: () => onClickDelete(tagOrGroup),
        },
      ]}
      selectedRows={
        deleteManyState.selecting ? deleteManyState.selectedIds : null
      }
      onChangeSelectedRows={tagIds =>
        onChangeDeleteManyState({
          selecting: true,
          selectedIds: tagIds,
          deleting: false,
        })
      }
      selectDisabled={deleteManyState.selecting && deleteManyState.deleting}
      getChildRows={nestedTagTableGetChildRows}
    />
  );
}

export function nestedTagTableGetChildRows<T>(
  tagOrGroup: { __typename?: "TagGroup"; tags: T[] } | { __typename?: "Tag" }
): T[] {
  if (tagOrGroup.__typename === "TagGroup") {
    return tagOrGroup.tags;
  }
  return [];
}

export function nestedTagTableRenderName(
  tagOrGroup: ListTagsTagOrGroup,
  { parent }: { parent?: ListTagsTagOrGroup }
): ReactElement {
  if (tagOrGroup.__typename === "TagGroup") {
    return (
      <Typography variant="body2Bold" sx={{ color: palette.darkGreen }}>
        {tagOrGroup.name}
      </Typography>
    );
  }

  if (parent) {
    return (
      <Box sx={{ ml: 4 }}>
        <TagChip label={tagOrGroup.name} />
      </Box>
    );
  }

  return <TagChip label={tagOrGroup.name} />;
}
